import { ControlData } from '@/services/soaUpdatesService';
import { db } from '@/lib/firebase';
import { doc, updateDoc } from 'firebase/firestore';

/**
 * Accept changes to an SOA control with assignee and status updates
 * @param tenancyId The ID of the customer tenancy
 * @param controlId The ID of the control to update
 * @param assignee The assignee to set for the control
 * @param status The status to set for the control
 * @returns A promise that resolves when the update is complete
 */
export const acceptSOAControlChanges = async (
  tenancyId: string,
  controlId: string,
  assignee: string,
  status: string
): Promise<void> => {
  try {
    // Reference to the control document using the correct path structure
    const controlRef = doc(db, `customers/${tenancyId}/product/policyPackages/ISO27001keyDocuments/ismsSoa/ism/${controlId}`);
    
    // Prepare the update data
    const updateData: Record<string, any> = {
      assignedTo: assignee || null,
      currentImplementationStatus: status || 'Planned',
      lastUpdated: new Date()
    };
    
    // Log the update
    console.log(`Updating SOA control ${controlId} with:`, updateData);
    
    // Update the document
    await updateDoc(controlRef, updateData);
    
    console.log(`Successfully updated SOA control ${controlId}`);
    
    return Promise.resolve();
  } catch (error) {
    console.error(`Error updating SOA control ${controlId}:`, error);
    return Promise.reject(error);
  }
};

/**
 * Save implementation plan for an SOA control
 * @param tenancyId The ID of the customer tenancy
 * @param controlId The ID of the control to update
 * @param implementationPlan The implementation plan text to save
 * @returns A promise that resolves when the update is complete
 */
export const saveSOAControlImplementationPlan = async (
  tenancyId: string,
  controlId: string,
  implementationPlan: string
): Promise<void> => {
  try {
    // Reference to the control document using the correct path structure
    const controlRef = doc(db, `customers/${tenancyId}/product/policyPackages/ISO27001keyDocuments/ismsSoa/ism/${controlId}`);
    
    // Prepare the update data
    const updateData = {
      'controlData.implementationPlan': implementationPlan,
      lastUpdated: new Date()
    };
    
    // Log the update
    console.log(`Saving implementation plan for SOA control ${controlId}`);
    
    // Update the document
    await updateDoc(controlRef, updateData);
    
    console.log(`Successfully saved implementation plan for SOA control ${controlId}`);
    
    return Promise.resolve();
  } catch (error) {
    console.error(`Error saving implementation plan for SOA control ${controlId}:`, error);
    return Promise.reject(error);
  }
};
